import { useMemo, type VoidFunctionComponent } from 'react';

import { type NoticeMatch, NoticeMatchCorrectionType } from '../../../../../../shared/types/routes/revisions';
import { useEditable } from '../../../../../hooks';
import { useCorrectNoticeFile } from '../../../../../hooks/api/correctionsV2/useCorrectNoticeFile';
import { BUTTON_SIZE, BUTTON_TYPE, FullContentTextArea, LabeledMultiTextInput } from '../../../../generic';
import Button from '../../../../generic/Button';

import styles from '../DependencyDrawer.module.less';

type Props = {
  dependencyRevisionLocator: string;
  notice: NoticeMatch;
  onToggleEdit: () => void;
};

type NewNoticeMatch = Required<Pick<NoticeMatch, 'contents' | 'copyrights'>>;

export const DependencyNoticeEditing: VoidFunctionComponent<Props> = ({
  dependencyRevisionLocator,
  notice,
  onToggleEdit,
}) => {
  const { mutateAsync: upsertNoticeFile, isPending: isUpsertingNoticeFile } =
    useCorrectNoticeFile(dependencyRevisionLocator);

  const initialNotice = useMemo(
    () => ({
      contents: notice.contents,
      copyrights: notice.copyrights || [],
    }),
    [notice],
  );
  const { data, isDirty, onUpdate, onSave, onReset } = useEditable<NewNoticeMatch>(initialNotice);

  const onSaveNotice = () => {
    upsertNoticeFile(
      [
        {
          correctionType: NoticeMatchCorrectionType.Edited,
          path: notice.path,
          contents: data.contents,
          copyrights: data.copyrights,
        },
      ],
      {
        onSuccess: () => {
          onSave();
          onToggleEdit();
        },
      },
    );
  };
  const onCancel = () => {
    onReset();
    onToggleEdit();
  };

  return (
    <>
      <FullContentTextArea
        label="Notice contents"
        value={data.contents}
        onChange={(contents) => {
          onUpdate({ contents });
        }}
        isResizable
      />
      <div className={styles.copyrights}>
        <LabeledMultiTextInput
          labelVariant="legacy"
          id="copyrights"
          values={data.copyrights}
          label="Copyrights"
          onChange={(copyrights) => {
            onUpdate({ copyrights });
          }}
        />
      </div>
      <div className={styles.copyrightsHelpText}>Press enter after entering each copyright.</div>
      <div className={styles.footer}>
        <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type={BUTTON_TYPE.PRIMARY}
          size={BUTTON_SIZE.MEDIUM}
          onClick={onSaveNotice}
          isDisabled={!isDirty || isUpsertingNoticeFile}
        >
          Save
        </Button>
      </div>
      <hr />
    </>
  );
};
